import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type Konva from 'konva';
import { useParams } from 'react-router-dom';
import { Canvas } from './Canvas';
import { StickyDragGhost } from './StickyDragGhost';
import { Toolbar } from './Toolbar';
import { PropertyPanel } from './PropertyPanel';
import { ConnectionPill } from './ConnectionPill';
import { HostPill } from './HostPill';
import { InvitePanel } from './InvitePanel';
import { JoinFlow } from './JoinFlow';
import { SettingsPopover } from './SettingsPopover';
import { ZoomControls } from './ZoomControls';
import { Minimap } from './Minimap';
import { EmptyBoardHint } from './EmptyBoardHint';
import { ContextMenu, type MenuEntry } from './ContextMenu';
import {
  bringToFront,
  deleteShapesOp,
  duplicateShapes,
  groupShapes,
  sendToBack,
  toggleLocked,
  ungroupShapes,
} from './shapeOps';
import type { Viewport, Size } from './viewport';
import { useBoardDoc, type ConnectionStatus, type BoardDoc } from './binding/useBoardDoc';
import { useShapes } from './binding/useShapes';
import { fitBBoxToContainer } from './viewport';
import { addShape, shapeBBox } from './binding/shapesMap';
import { makeSticky, WIDE_STICKY_H, WIDE_STICKY_W } from './tools';
import {
  readStickyColor,
  readStickySize,
  writeStickyColor,
} from './stickyPrefs';
import type { StickyColor } from '@frame/shared';
import { useLocalUser } from '../awareness/useLocalUser';
import { useAwareness } from '../awareness/useAwareness';
import { PresenceStrip } from '../awareness/PresenceStrip';
import { rememberRecentBoard } from '../home/recentBoards';
import type { AwarenessState, AwarenessUser } from '../awareness/types';
import { CURSOR_PALETTE } from '../styles/tokens';
import type { ToolId } from './tools';
import { useUndoManager, useUndoShortcuts } from './useUndo';
import { useToolShortcuts } from './useToolShortcuts';
import { useShapeShortcuts } from './useShapeShortcuts';
// Retro layer — meta + voting + chrome. Mounts only when meta.mode === 'retro'.
import { useRetroMeta } from '../retro/useRetroMeta';
import { useVotesUsed } from '../retro/useVotes';
import { useVoteOnClick } from '../retro/useVoteOnClick';
import { anonymizeShapes } from '../retro/anonymize';
import { PhaseBar } from '../retro/components/PhaseBar';
import { TimerWidget } from '../retro/components/TimerWidget';
import { ModeToggle } from '../retro/components/ModeToggle';
import { VoteBudgetPill } from '../retro/components/VoteBudgetPill';
import { VoteIndicators } from '../retro/components/VoteIndicators';
import { ActionItemsSidebar } from '../retro/components/ActionItemsSidebar';

export function BoardPage() {
  const { boardId } = useParams<{ boardId: string }>();
  const id = boardId ?? 'test';
  const [localUser, updateLocalUser] = useLocalUser();
  const { bundle, status, peerCount } = useBoardDoc(id, localUser.id);

  useEffect(() => {
    rememberRecentBoard(id);
  }, [id]);

  // Loading shell renders the same 3-column layout so the chrome doesn't shift
  // when IndexedDB finishes replaying. Only the canvas-area contents differ.
  if (!bundle) {
    return (
      <BoardLayout
        toolbar={
          <Toolbar
            activeTool="select"
            onSelectTool={() => {}}
            stickyColor="sand"
            onPickStickyColor={() => {}}
          />
        }
        canvas={
          <StatusBar>
            <ConnectionPill status={status} peerCount={peerCount} />
          </StatusBar>
        }
        sidebar={<PropertyPanel shapes={[]} selectedIds={new Set()} boardId={id} />}
      />
    );
  }

  return (
    <LiveBoard
      bundle={bundle}
      boardId={id}
      status={status}
      peerCount={peerCount}
      localUser={localUser}
      updateLocalUser={updateLocalUser}
    />
  );
}

interface LiveBoardProps {
  bundle: BoardDoc;
  boardId: string;
  status: ConnectionStatus;
  peerCount: number;
  localUser: AwarenessUser;
  updateLocalUser: (patch: Partial<Pick<AwarenessUser, 'name' | 'colorIndex'>>) => void;
}

function LiveBoard({
  bundle,
  boardId,
  status,
  peerCount,
  localUser,
  updateLocalUser,
}: LiveBoardProps) {
  const { doc, provider } = bundle;
  const { peers, peerSelections, setLocalState } = useAwareness(provider);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [activeTool, setActiveTool] = useState<ToolId>('select');
  const shapes = useShapes(doc);
  const [joinOffer, setJoinOffer] = useState<string | null>(() => readJoinFromHash());
  // Captured once at mount. After the user accepts the invite the hash gets
  // stripped, but a guest tab should still read as guest for the whole session.
  const [isHost] = useState(() => readJoinFromHash() === null);
  const [settingsAnchor, setSettingsAnchor] = useState<DOMRect | null>(null);

  const [viewport, setViewport] = useState<Viewport>({ x: 0, y: 0, scale: 1 });
  const [canvasSize, setCanvasSize] = useState<Size>({ width: 0, height: 0 });
  // Cached Konva stage reference for the exporters. Canvas hands it up via
  // onStageReady on mount; exporters read .current at click time.
  const stageRef = useRef<Konva.Stage | null>(null);
  const handleStageReady = useCallback((stage: Konva.Stage) => {
    stageRef.current = stage;
  }, []);
  const [menu, setMenu] = useState<{ x: number; y: number; shapeId: string | null } | null>(null);

  // Active sticky color — fed to the toolbar's color popover and used as the
  // default fill for the next sticky create (canvas click OR drag drop).
  // Initialized from the last-picked color in localStorage so a returning
  // user sees their previous choice highlighted.
  const [stickyColor, setStickyColor] = useState<StickyColor>(
    () => readStickyColor() ?? 'sand',
  );
  const handlePickStickyColor = useCallback((color: StickyColor) => {
    setStickyColor(color);
    writeStickyColor(color);
  }, []);

  // Sticky drag-from-sidebar. When the Toolbar reports the start of a drag,
  // we install window-level pointer listeners that follow the cursor, render
  // a DOM ghost, and on release either create the sticky (if dropped over
  // the canvas) or cancel silently. The state stays null when no drag is
  // active so the ghost overlay unmounts.
  const [stickyDrag, setStickyDrag] = useState<{
    variant: 'square' | 'wide';
    color: StickyColor;
    screenX: number;
    screenY: number;
  } | null>(null);
  const handleStartStickyDrag = useCallback(
    (
      variant: 'square' | 'wide',
      color: StickyColor | undefined,
      screenPos: { x: number; y: number },
    ) => {
      // No explicit color (rail-icon drag) → use the currently armed color.
      const effective = color ?? stickyColor;
      setStickyDrag({ variant, color: effective, screenX: screenPos.x, screenY: screenPos.y });
    },
    [stickyColor],
  );
  useEffect(() => {
    if (!stickyDrag) return;
    const onMove = (e: PointerEvent) => {
      setStickyDrag((prev) =>
        prev ? { ...prev, screenX: e.clientX, screenY: e.clientY } : prev,
      );
    };
    const onUp = (e: PointerEvent) => {
      const stage = stageRef.current;
      if (stage) {
        const rect = stage.container().getBoundingClientRect();
        const insideCanvas =
          e.clientX >= rect.left &&
          e.clientX <= rect.right &&
          e.clientY >= rect.top &&
          e.clientY <= rect.bottom;
        if (insideCanvas) {
          const stagePtX = e.clientX - rect.left;
          const stagePtY = e.clientY - rect.top;
          const worldX = (stagePtX - viewport.x) / viewport.scale;
          const worldY = (stagePtY - viewport.y) / viewport.scale;
          writeStickyColor(stickyDrag.color);
          const size =
            stickyDrag.variant === 'wide'
              ? { w: WIDE_STICKY_W, h: WIDE_STICKY_H }
              : readStickySize();
          const sticky = makeSticky(
            { x: worldX, y: worldY },
            { authorId: localUser.id },
            size,
            stickyDrag.color,
          );
          addShape(doc, sticky);
          setSelectedIds(new Set([sticky.id]));
        }
      }
      setStickyDrag(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setStickyDrag(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('keydown', onKey);
    };
  }, [stickyDrag, doc, localUser.id, viewport.x, viewport.y, viewport.scale]);

  const openMenu = useCallback(
    (info: { screenX: number; screenY: number; shapeId: string | null }) => {
      setMenu({ x: info.screenX, y: info.screenY, shapeId: info.shapeId });
    },
    [],
  );
  const dismissMenu = useCallback(() => setMenu(null), []);

  const menuEntries = useMemo<MenuEntry[]>(() => {
    if (!menu) return [];
    const onShape = menu.shapeId !== null;
    const ids = onShape ? new Set(selectedIds.size > 0 ? selectedIds : [menu.shapeId!]) : selectedIds;
    const hasTargets = ids.size > 0;
    const targetShapes = hasTargets ? shapes.filter((s) => ids.has(s.id)) : [];
    // Group submenu label flips based on whether the selection already
    // forms a single group (then "Ungroup"). Mixed-group / no-group set →
    // "Group" creates a fresh group.
    const allLocked = targetShapes.length > 0 && targetShapes.every((s) => s.locked);
    const someGrouped = targetShapes.some((s) => s.groupId);
    const allSameGroup =
      targetShapes.length > 0 &&
      !!targetShapes[0]?.groupId &&
      targetShapes.every((s) => s.groupId === targetShapes[0]?.groupId);
    const out: MenuEntry[] = [];
    if (hasTargets) {
      out.push({
        label: 'Duplicate',
        shortcut: '⌘D',
        onClick: () => {
          const newIds = duplicateShapes(doc, ids, localUser.id);
          if (newIds.length > 0) setSelectedIds(new Set(newIds));
        },
      });
      out.push('separator');
      if (ids.size >= 2 && !allSameGroup) {
        out.push({
          label: 'Group',
          shortcut: '⌘G',
          onClick: () => {
            groupShapes(doc, ids);
          },
        });
      }
      if (someGrouped) {
        out.push({
          label: 'Ungroup',
          shortcut: '⇧⌘G',
          onClick: () => ungroupShapes(doc, ids),
        });
      }
      out.push({
        label: allLocked ? 'Unlock' : 'Lock',
        shortcut: '⇧⌘L',
        onClick: () => toggleLocked(doc, ids),
      });
      out.push('separator');
      out.push({
        label: 'Bring to front',
        shortcut: '⌘]',
        onClick: () => bringToFront(doc, ids),
      });
      out.push({
        label: 'Send to back',
        shortcut: '⌘[',
        onClick: () => sendToBack(doc, ids),
      });
      out.push('separator');
      out.push({
        label: 'Delete',
        shortcut: '⌫',
        destructive: true,
        disabled: allLocked,
        onClick: () => {
          deleteShapesOp(doc, ids);
          setSelectedIds(new Set());
        },
      });
    } else {
      out.push({
        label: 'Paste',
        shortcut: '⌘V',
        disabled: true,
        onClick: () => {},
      });
    }
    return out;
  }, [menu, selectedIds, doc, localUser.id, shapes]);

  const undoManager = useUndoManager(doc);
  useUndoShortcuts(undoManager);
  useToolShortcuts(setActiveTool);
  useShapeShortcuts({ doc, selectedIds, setSelectedIds, authorId: localUser.id });

  // Retro meta drives chrome visibility + behavioral overrides (anonymize
  // shapes during write phase, vote-on-click during vote phase, etc.).
  const retroMeta = useRetroMeta(doc);
  const isRetro = retroMeta.mode === 'retro';
  const votesUsed = useVotesUsed(doc, localUser.id);
  // shapesById is consumed by useVoteOnClick. Compute once per shapes change.
  const shapesById = useMemo(() => new Map(shapes.map((s) => [s.id, s])), [shapes]);
  useVoteOnClick({
    doc,
    phase: retroMeta.phase,
    voteBudget: retroMeta.voteBudget ?? 0,
    userId: localUser.id,
    selectedIds,
    setSelectedIds,
    shapesById,
  });

  // Render shapes pass-through normally; during anonymous write phase
  // mask other-author text content. Voting indicators read from the
  // unmasked shapes (anonymize never touches bboxes).
  const renderShapes = useMemo(
    () => anonymizeShapes(shapes, retroMeta, localUser.id),
    [shapes, retroMeta, localUser.id],
  );

  // Center the viewport on a sticky when the action-sidebar row is clicked.
  const focusShape = useCallback(
    (shapeId: string) => {
      const shape = shapesById.get(shapeId);
      if (!shape) return;
      const bbox = shapeBBox(shape);
      if (!bbox) return;
      setSelectedIds(new Set([shapeId]));
      setViewport(fitBBoxToContainer(bbox, canvasSize, 200));
    },
    [shapesById, canvasSize],
  );

  // Voting indicators visible during vote / discuss / actions phases so the
  // room can see the tally beyond the active voting window.
  const showVoteIndicators =
    isRetro && (retroMeta.phase === 'vote' || retroMeta.phase === 'discuss' || retroMeta.phase === 'actions');
  const showActionsSidebar = isRetro && retroMeta.phase === 'actions';

  // The hash can change without a route change (e.g. clicking a fresh invite
  // link in the same tab) — listen for hashchange too.
  useEffect(() => {
    const onHash = () => setJoinOffer(readJoinFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const dismissJoin = useCallback(() => {
    setJoinOffer(null);
    // Strip the hash without a navigation so refresh doesn't reopen the modal.
    if (window.location.hash) {
      const url = new URL(window.location.href);
      url.hash = '';
      window.history.replaceState(null, '', url.toString());
    }
  }, []);

  // Initialize local awareness on mount; cursor + selection + activeTool
  // updates flow through provider.awareness.setLocalStateField from Canvas.
  useEffect(() => {
    const initial: AwarenessState = {
      user: localUser,
      cursor: null,
      selection: [],
      activeTool: 'select',
    };
    setLocalState(initial);
  }, [localUser, setLocalState]);

  // Broadcast selection whenever it changes locally so peers see colored rings.
  useEffect(() => {
    provider.awareness.setLocalStateField('selection', [...selectedIds]);
  }, [selectedIds, provider.awareness]);

  const peerSelectionColors = useMemo(() => {
    const out = new Map<string, { color: string }[]>();
    peerSelections.forEach((peerList, shapeId) => {
      out.set(
        shapeId,
        peerList.map((p) => ({ color: cursorColorForIndex(p.colorIndex) })),
      );
    });
    return out;
  }, [peerSelections]);

  return (
    <>
      <BoardLayout
        toolbar={
          <Toolbar
            activeTool={activeTool}
            onSelectTool={setActiveTool}
            onOpenSettings={setSettingsAnchor}
            onStartStickyDrag={handleStartStickyDrag}
            stickyColor={stickyColor}
            onPickStickyColor={handlePickStickyColor}
          />
        }
        canvas={
          <>
            <Canvas
              doc={doc}
              provider={provider}
              peers={peers}
              peerSelectionColors={peerSelectionColors}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              activeTool={activeTool}
              setActiveTool={setActiveTool}
              authorId={localUser.id}
              viewport={viewport}
              setViewport={setViewport}
              onSizeChange={setCanvasSize}
              onContextMenu={openMenu}
              shapesOverride={isRetro ? renderShapes : undefined}
              onStageReady={handleStageReady}
            />
            {shapes.length === 0 && <EmptyBoardHint isHost={isHost} />}
            <PresenceStrip localUser={localUser} peers={peers} />
            <InvitePanel provider={provider} />
            <ZoomControls
              viewport={viewport}
              setViewport={setViewport}
              containerSize={canvasSize}
              shapes={shapes}
            />
            <Minimap
              viewport={viewport}
              setViewport={setViewport}
              containerSize={canvasSize}
              shapes={shapes}
            />
            {isRetro && (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
                <PhaseBar doc={doc} phase={retroMeta.phase} isFacilitator={isHost} />
                <TimerWidget doc={doc} timer={retroMeta.timer} isFacilitator={isHost} />
              </div>
            )}
            {showVoteIndicators && (
              <VoteIndicators doc={doc} shapes={shapes} viewport={viewport} />
            )}
            {isRetro && retroMeta.phase === 'vote' && (
              <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-10">
                <VoteBudgetPill used={votesUsed} budget={retroMeta.voteBudget ?? 0} />
              </div>
            )}
            {showActionsSidebar && (
              <ActionItemsSidebar doc={doc} shapes={shapes} onSelectShape={focusShape} />
            )}
            <StatusBar>
              {isHost && <HostPill />}
              <ModeToggle doc={doc} mode={retroMeta.mode} isHost={isHost} />
              <ConnectionPill status={status} peerCount={peerCount} />
            </StatusBar>
            {joinOffer && (
              <JoinFlow provider={provider} offerCode={joinOffer} onDismiss={dismissJoin} />
            )}
          </>
        }
        sidebar={<PropertyPanel shapes={shapes} selectedIds={selectedIds} boardId={boardId} />}
      />
      {settingsAnchor && (
        <SettingsPopover
          user={localUser}
          onUpdate={updateLocalUser}
          boardId={boardId}
          onClose={() => setSettingsAnchor(null)}
          anchorRect={settingsAnchor}
          shapes={shapes}
          stageRef={stageRef}
        />
      )}
      {menu && (
        <ContextMenu x={menu.x} y={menu.y} entries={menuEntries} onDismiss={dismissMenu} />
      )}
      {stickyDrag && (
        <StickyDragGhost
          variant={stickyDrag.variant}
          color={stickyDrag.color}
          screenX={stickyDrag.screenX}
          screenY={stickyDrag.screenY}
          scale={viewport.scale}
        />
      )}
    </>
  );
}

function BoardLayout({
  toolbar,
  canvas,
  sidebar,
}: {
  toolbar: ReactNode;
  canvas: ReactNode;
  sidebar: ReactNode;
}) {
  return (
    <div className="absolute inset-0 grid grid-cols-[56px_1fr_280px] bg-bg-app">
      {toolbar}
      <div className="relative bg-bg-canvas overflow-hidden">
        <DotGrid />
        {canvas}
      </div>
      {sidebar}
    </div>
  );
}

// Mirrors tokens.cursors / globals.css `--cursor-N`. Used for peer selection
// rings; the canvas-side <RemoteCursors> reads its colors from useTokens
// (which is inside the React tree).
function cursorColorForIndex(idx: number): string {
  return CURSOR_PALETTE[idx % CURSOR_PALETTE.length]!;
}

function DotGrid() {
  return <div aria-hidden className="absolute inset-0 dot-grid pointer-events-none" />;
}

// Status row anchored to the bottom-right of the canvas. Holds whatever
// session-state pills want to live there — currently HostPill + ConnectionPill.
function StatusBar({ children }: { children: ReactNode }) {
  return (
    <div className="absolute right-3 bottom-3 z-[3] flex items-center gap-2">
      {children}
    </div>
  );
}

// Parse `#join=<offerCode>` (URL-decoded). Returns null if absent so the
// JoinFlow stays unmounted on a normal open.
function readJoinFromHash(): string | null {
  if (typeof window === 'undefined') return null;
  const raw = window.location.hash;
  if (!raw) return null;
  const params = new URLSearchParams(raw.startsWith('#') ? raw.slice(1) : raw);
  const offer = params.get('join');
  return offer && offer.length > 0 ? offer : null;
}
